(function () {
  'use strict';

  if (customElements.get('product-variant-small')) return;

  class ProductVariantSmall extends HTMLElement {
    connectedCallback() {
      this.changeEvent = this.changeEvent || this.onChange.bind(this);
      this.addEventListener('change', this.changeEvent, true);
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.changeEvent, true);
    }

    restoreSelection(input, previousInput, status) {
      input.checked = false;
      if (previousInput) previousInput.checked = true;
      if (status) status.textContent = 'Unable to load product';
    }

    keepSwitcherPosition(nextSwitcher, originalTop, scrollContainer, productId) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!nextSwitcher?.isConnected) return;

          const offset = nextSwitcher.getBoundingClientRect().top - originalTop;
          if (Math.abs(offset) > 1) {
            if (scrollContainer?.isConnected) {
              scrollContainer.scrollTop += offset;
            } else {
              window.scrollBy({ top: offset, left: 0, behavior: 'auto' });
            }
          }

          nextSwitcher
            .querySelector(`[data-small-product-option][data-product-id="${productId}"]`)
            ?.focus({ preventScroll: true });
        });
      });
    }

    replaceProductPage(currentProduct, parsedDocument, sectionId) {
      const nextProduct = parsedDocument.getElementById(currentProduct.id)
        || parsedDocument.querySelector(`product-component[data-section-id="${sectionId}"]`);

      if (!nextProduct) throw new Error('Product section was not found in the response');

      const currentProductJson = currentProduct.querySelector('[data-product-json]');
      const nextProductJson = nextProduct.querySelector('[data-product-json]');
      const currentProductContent = currentProduct.querySelector('[data-product]');
      const nextProductContent = nextProduct.querySelector('[data-product]');
      const currentCartBar = currentProduct.querySelector('#cart-bar');
      const nextCartBar = nextProduct.querySelector('#cart-bar');

      if (!currentProductJson || !nextProductJson || !currentProductContent || !nextProductContent) {
        throw new Error('Product content was not found in the response');
      }

      currentProductJson.replaceWith(nextProductJson);
      currentProduct.dataset.productHandle = nextProduct.dataset.productHandle;
      currentProduct.setAttribute('data-enable-history-state', 'false');
      currentProductContent.replaceWith(nextProductContent);

      if (currentCartBar && nextCartBar) currentCartBar.replaceWith(nextCartBar);

      const currentFinalSaleData = currentProduct.querySelector('[data-variant-final-sale-metafield]');
      const nextFinalSaleData = nextProduct.querySelector('[data-variant-final-sale-metafield]');
      if (currentFinalSaleData && nextFinalSaleData) {
        currentFinalSaleData.replaceWith(nextFinalSaleData);
      }

      const productForm = currentProduct.querySelector('product-form');
      if (productForm) {
        productForm.enableHistoryState = false;
        if (productForm.variantEngine) productForm.variantEngine.onChange = () => {};
      }

      currentProduct.form = currentProduct.querySelector('[data-product-form]');
      currentProduct.formWrapper = currentProduct.querySelector('[data-form-wrapper]');
      currentProduct.cartBar = currentProduct.querySelector('#cart-bar');
      if (currentProduct.cartBar && typeof currentProduct.initCartBar === 'function') {
        currentProduct.initCartBar();
      }

      return currentProduct.querySelector('product-variant-small');
    }

    replaceQuickAdd(quickAddModal, parsedDocument, productId) {
      const nextApiContent = parsedDocument.querySelector('[data-api-content]');
      const currentApiContent = quickAddModal.querySelector('[data-product-upsell-ajax]');
      if (!nextApiContent || !currentApiContent) {
        throw new Error('Quick-add product content was not found in the response');
      }

      currentApiContent.innerHTML = nextApiContent.innerHTML;
      quickAddModal.dataset.productId = productId;

      // The initial quick-add open animates these nodes from the modal
      // controller. After an in-place product swap that callback does not run,
      // so reveal the newly inserted title, price, variants and cart button.
      requestAnimationFrame(() => {
        currentApiContent.querySelectorAll('[data-animation]').forEach((element) => {
          element.classList.add('is-animated');
        });
      });

      return currentApiContent.querySelector('product-variant-small');
    }

    async onChange(event) {
      const input = event.target.closest('[data-small-product-option]');
      if (!input || !input.dataset.productUrl || this.loading) return;

      event.stopImmediatePropagation();

      const previousInput = this.querySelector('[data-small-product-option][checked]');
      const currentProduct = this.closest('product-component[data-section-id]');
      const quickAddModal = this.closest('[data-quick-add-modal]');
      const switchingContainer = currentProduct || quickAddModal;
      const status = this.querySelector('.product-variant-small__status');

      if (!switchingContainer) {
        this.restoreSelection(input, previousInput, status);
        return;
      }

      const productUrl = new URL(input.dataset.productUrl, window.location.origin);
      const sectionId = currentProduct?.dataset.sectionId || 'api-product-upsell';
      const requestUrl = new URL(productUrl.href);
      requestUrl.searchParams.set('section_id', sectionId);
      const originalTop = this.getBoundingClientRect().top;
      const scrollContainer = quickAddModal ? this.closest('[data-scroll-lock-scrollable]') : null;

      this.loading = true;
      switchingContainer.classList.add('is-switching-small-product');
      switchingContainer.setAttribute('aria-busy', 'true');
      if (status) status.textContent = 'Loading product';

      try {
        const response = await fetch(requestUrl.href, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!response.ok) throw new Error(`Product request failed: ${response.status}`);

        const responseText = await response.text();
        const parsedDocument = new DOMParser().parseFromString(responseText, 'text/html');
        const nextSwitcher = currentProduct
          ? this.replaceProductPage(currentProduct, parsedDocument, sectionId)
          : this.replaceQuickAdd(quickAddModal, parsedDocument, input.dataset.productId);

        switchingContainer.classList.remove('is-switching-small-product');
        switchingContainer.removeAttribute('aria-busy');
        this.keepSwitcherPosition(nextSwitcher, originalTop, scrollContainer, input.dataset.productId);

        document.dispatchEvent(new CustomEvent('theme:small-product:change', {
          detail: {
            productUrl: productUrl.href,
            productId: input.dataset.productId,
            sectionId,
            quickAdd: Boolean(quickAddModal),
          },
        }));
      } catch (error) {
        console.error(error);
        switchingContainer.classList.remove('is-switching-small-product');
        switchingContainer.removeAttribute('aria-busy');
        this.restoreSelection(input, previousInput, status);
      } finally {
        this.loading = false;
      }
    }
  }

  customElements.define('product-variant-small', ProductVariantSmall);
})();
